import React, { useEffect, useState } from 'react';
import {
  Container,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  Select,
  SelectChangeEvent,
  Tabs,
  Grid,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTab as Tab, LocalizedTableCell as TableCell } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BirthChartDisplay from '../components/charts/BirthChartDisplay';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useChart } from '../contexts/ChartContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { calculateDashaSubPeriods, calculateRuntimeDasha } from '../utils/dashaCalculator';
import { FREE_LIFE_EVENT_KEYS, lifeEvents } from '../utils/lifeEvents';
import { getHouseShort, getPlanetAbbr } from '../i18n/astrology';
import VedicYogasPanel from '../components/vedic/VedicYogasPanel';
import { useVedicYogas } from '../hooks/useVedicYogas';
import { isOuterPlanetKey, useUserPreferences } from '../contexts/UserPreferencesContext';
import { formatDisplayDate } from '../utils/dateDisplay';
import DashaAnalysisPanel, { dashaAnalysisLabel } from '../components/dasha/DashaAnalysisPanel';
import EventAnalysisPanel from '../components/nadi/EventAnalysisPanel';
import { translateNadiEventText } from '../data/nadiEventTranslations';
import CollapsibleDashaTimeline from '../components/dasha/CollapsibleDashaTimeline';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const displayChartName = (value?: string | null) => String(value || '').trim() || 'N/A';
const NEW_DELHI_LOCATION = {
  city: 'New Delhi',
  latitude: 28.6139,
  longitude: 77.2090,
  timezoneOffset: 330,
};

const parseTimezoneOffset = (value: any) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const match = value.trim().match(/^([+-])?(\d{1,2})(?::?(\d{2}))?$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      return sign * ((Number(match[2]) || 0) * 60 + (Number(match[3]) || 0));
    }
  }
  return 330;
};

const getDefaultRulingLocation = (defaultCity: any) => {
  if (!defaultCity?.coordinates) return NEW_DELHI_LOCATION;
  return {
    city: defaultCity.displayName || defaultCity.name || 'New Delhi',
    latitude: defaultCity.coordinates.latitude,
    longitude: defaultCity.coordinates.longitude,
    timezoneOffset: parseTimezoneOffset(defaultCity.timezone?.offset),
  };
};

const getLocalDateTimeForOffset = (timezoneOffset: number) => {
  const local = new Date(Date.now() + timezoneOffset * 60000);
  return {
    date: local.toISOString().slice(0, 10),
    time: local.toISOString().slice(11, 19),
  };
};

const rulingPlanetItems = (rulingPlanets: any) => [
  { label: 'Lg Sign', title: 'Lagna Sign Lord', value: rulingPlanets?.lagnaLord },
  { label: 'Lg Star', title: 'Lagna Star Lord', value: rulingPlanets?.lagnaStarLord },
  { label: 'Mo Sign', title: 'Moon Sign Lord', value: rulingPlanets?.moonSignLord },
  { label: 'Mo Star', title: 'Moon Star Lord', value: rulingPlanets?.moonStarLord },
  { label: 'Day', title: 'Day Lord', value: rulingPlanets?.dayLord },
];

const ChartDisplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentChart } = useChart();
  const { state: authState } = useAuth();
  const { i18n, t } = useTranslation();
  const { showOuterPlanets, defaultCity } = useUserPreferences();
  const rulingLocation = React.useMemo(() => getDefaultRulingLocation(defaultCity), [defaultCity]);
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<string>('none');
  const [selectedChartTab, setSelectedChartTab] = useState<number>(0);
  const [chartRotationHouse, setChartRotationHouse] = useState<number>(1);
  const [selectedMahadashaKey, setSelectedMahadashaKey] = useState<string | null>(null);
  const [selectedAntardashaKey, setSelectedAntardashaKey] = useState<string | null>(null);
  const [selectedPratyantardashaKey, setSelectedPratyantardashaKey] = useState<string | null>(null);
  const [selectedSookshmaKey, setSelectedSookshmaKey] = useState<string | null>(null);
  const [selectedPranaKey, setSelectedPranaKey] = useState<string | null>(null);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [rectificationLoading, setRectificationLoading] = useState(false);
  const [saveRectificationLoading, setSaveRectificationLoading] = useState(false);
  const [rectificationMessage, setRectificationMessage] = useState<string | null>(null);
  const [currentRulingPlanets, setCurrentRulingPlanets] = useState<any>(null);
  const [currentRulingError, setCurrentRulingError] = useState<string | null>(null);
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const effectiveLifeEvent = isPremiumUser || FREE_LIFE_EVENT_KEYS.has(selectedLifeEvent)
    ? selectedLifeEvent
    : 'none';
  const getAuthToken = () => authState.token || localStorage.getItem('nadiAstroToken') || '';

  const getActualHouseForDisplay = React.useCallback(
    (displayHouse: number) => ((chartRotationHouse + displayHouse - 2) % 12) + 1,
    [chartRotationHouse]
  );

  const toRotatedHouse = React.useCallback(
    (house: number) => ((house - chartRotationHouse + 12) % 12) + 1,
    [chartRotationHouse]
  );

  const rotateHouseList = React.useCallback(
    (houses: number[]) => houses.map((house) => toRotatedHouse(Number(house))).filter((house) => house >= 1 && house <= 12),
    [toRotatedHouse]
  );

  const handleLifeEventChange = (event: SelectChangeEvent<string>) => {
    const nextEvent = event.target.value;
    if (isPremiumUser || FREE_LIFE_EVENT_KEYS.has(nextEvent)) {
      setSelectedLifeEvent(nextEvent);
    }
  };

  const handleChartTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedChartTab(newValue);
  };

  useEffect(() => {
    // Set page-specific document title
    document.title = 'Chart Display - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
    
    if (id === 'preview') {
      // Use chart from context for preview
      if (currentChart) {
        setChartData(currentChart);
        setLoading(false);
      } else {
        setError('No chart data available for preview');
        setLoading(false);
      }
    } else {
      fetchChart();
    }
  }, [id, currentChart]);

  useEffect(() => {
    const loadCurrentRulingPlanets = async () => {
      try {
        setCurrentRulingError(null);
        const localNow = getLocalDateTimeForOffset(rulingLocation.timezoneOffset);
        const response = await axios.post(`${API_BASE_URL}/charts/ruling-planets`, {
          city: rulingLocation.city,
          latitude: rulingLocation.latitude,
          longitude: rulingLocation.longitude,
          timezoneOffset: rulingLocation.timezoneOffset,
          date: localNow.date,
          time: localNow.time,
        });
        setCurrentRulingPlanets(response.data.data.rulingPlanets);
      } catch (error: any) {
        setCurrentRulingError(error?.response?.data?.message || 'Unable to load ruling planets');
      }
    };

    loadCurrentRulingPlanets();
  }, [rulingLocation]);

  const fetchChart = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/charts/${id}`, {
        params: { module: 'nadi', ayanamshaMode: 'KP' },
      });
      const chart = response.data.data.chart;
      setChartData(chart);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load chart');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDisplayDate(dateString, { timeZone: 'UTC' });
  };

  const formatTime = (timeObj: any) => {
    if (!timeObj) return '';
    const hour = timeObj.hour || 0;
    const minute = timeObj.minute || 0;
    const second = timeObj.second || 0;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
  };

  const toISODateString = (date: Date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getCurrentRectificationBase = () => {
    const birthDate = new Date(chartData.birthDetails.dateOfBirth);
    const timeObj = chartData.birthDetails.timeOfBirth || {};
    const dt = new Date(Date.UTC(
      birthDate.getUTCFullYear(),
      birthDate.getUTCMonth(),
      birthDate.getUTCDate(),
      timeObj.hour || 0,
      timeObj.minute || 0,
      timeObj.second || 0
    ));
    return dt;
  };

  const runRectificationRecalculation = async (adjustedDateTimeUtc: Date) => {
    const safeChartSettings = {
      chartStyle: chartData.chartSettings?.chartStyle || 'North',
      language: chartData.chartSettings?.language || 'Hinglish'
    };

    const payload = {
      name: chartData.birthDetails.name,
      dateOfBirth: toISODateString(adjustedDateTimeUtc),
      timeOfBirth: {
        hour: adjustedDateTimeUtc.getUTCHours(),
        minute: adjustedDateTimeUtc.getUTCMinutes(),
        second: adjustedDateTimeUtc.getUTCSeconds()
      },
      placeOfBirth: chartData.birthDetails.placeOfBirth,
      gender: chartData.birthDetails.gender,
      saveChart: false,
      calculationModule: 'nadi',
      chartSettings: safeChartSettings
    };

    const token = getAuthToken();
    const isHorary = chartData?.chartType === 'horary';
    const horaryPayload = {
      name: chartData.birthDetails.name,
      question: chartData.horaryMeta?.question || '',
      seedNumber: chartData.horaryMeta?.seedNumber,
      dateOfQuery: toISODateString(adjustedDateTimeUtc),
      timeOfQuery: payload.timeOfBirth,
      placeOfQuery: chartData.birthDetails.placeOfBirth,
      saveChart: false,
      chartSettings: safeChartSettings,
    };
    const response = await axios.post(`${API_BASE_URL}/charts/${isHorary ? 'horary' : 'calculate'}`, isHorary ? horaryPayload : payload, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    setChartData({
      ...response.data.data.chart,
      _id: chartData._id || (id !== 'preview' ? id : undefined),
      id: chartData.id,
      userId: chartData.userId,
    });
  };

  const handleRectificationAdjust = async (
    unit: 'month' | 'day' | 'hour' | 'minute' | 'second',
    delta: number
  ) => {
    try {
      setRectificationLoading(true);
      setRectificationMessage(null);

      const adjusted = getCurrentRectificationBase();
      if (unit === 'month') adjusted.setUTCMonth(adjusted.getUTCMonth() + delta);
      if (unit === 'day') adjusted.setUTCDate(adjusted.getUTCDate() + delta);
      if (unit === 'hour') adjusted.setUTCHours(adjusted.getUTCHours() + delta);
      if (unit === 'minute') adjusted.setUTCMinutes(adjusted.getUTCMinutes() + delta);
      if (unit === 'second') adjusted.setUTCSeconds(adjusted.getUTCSeconds() + delta);

      await runRectificationRecalculation(adjusted);
    } catch (err: any) {
      setRectificationMessage(err.response?.data?.message || 'Rectification recalculation failed');
    } finally {
      setRectificationLoading(false);
    }
  };

  const handleSaveRectification = async () => {
    if (id === 'preview') {
      setRectificationMessage('Preview charts cannot be saved. Please save the chart first.');
      return;
    }

    try {
      setSaveRectificationLoading(true);
      setRectificationMessage(null);

      const token = getAuthToken();
      if (!token) {
        setRectificationMessage('Please login to save rectification.');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/charts/${id}/rectify`,
        {
          dateOfBirth: chartData.birthDetails.dateOfBirth,
          timeOfBirth: chartData.birthDetails.timeOfBirth,
          calculationModule: 'nadi'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setChartData(response.data.data.chart);
      setRectificationMessage('Rectified chart saved successfully.');
    } catch (err: any) {
      setRectificationMessage(err.response?.data?.message || 'Failed to save rectification');
    } finally {
      setSaveRectificationLoading(false);
    }
  };

  const getRectifiedDateTimeLabel = () => {
    const d = new Date(chartData.birthDetails.dateOfBirth);
    const t = chartData.birthDetails.timeOfBirth || {};
    const dt = new Date(Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      t.hour || 0,
      t.minute || 0,
      t.second || 0
    ));

    return `${formatDisplayDate(dt, { timeZone: 'UTC' })} ${
      `${String(dt.getUTCHours()).padStart(2, '0')}:${String(dt.getUTCMinutes()).padStart(2, '0')}:${String(dt.getUTCSeconds()).padStart(2, '0')}`
    }`;
  };

  const dashaData = React.useMemo(() => calculateRuntimeDasha(chartData), [chartData]);
  const { yogas, loading: yogasLoading, error: yogasError } = useVedicYogas(chartData, {
    enabled: isPremiumUser,
    token: authState.token,
  });

  const getDashaEntryKey = (entry: any) => {
    return entry ? `${entry.lord}-${entry.start?.toISOString?.() || ''}` : null;
  };

  const selectedMahadasha = React.useMemo(() => {
    if (!dashaData) return null;
    return dashaData.mahadashas.find((entry: any) => getDashaEntryKey(entry) === selectedMahadashaKey) || dashaData.currentMahadasha;
  }, [dashaData, selectedMahadashaKey]);

  const selectedAntardasha = React.useMemo(() => {
    if (!selectedMahadasha) return null;
    return selectedMahadasha.antardashas?.find((entry: any) => getDashaEntryKey(entry) === selectedAntardashaKey)
      || selectedMahadasha.antardashas?.[0]
      || null;
  }, [selectedMahadasha, selectedAntardashaKey]);

  const selectedPratyantardasha = React.useMemo(() => {
    if (!selectedAntardasha) return null;
    return selectedAntardasha.pratyantardashas?.find((entry: any) => getDashaEntryKey(entry) === selectedPratyantardashaKey)
      || selectedAntardasha.pratyantardashas?.find((entry: any) =>
        entry.lord === dashaData?.currentPratyantardasha?.lord &&
        entry.start?.getTime?.() === dashaData?.currentPratyantardasha?.start?.getTime?.()
      )
      || selectedAntardasha.pratyantardashas?.[0]
      || null;
  }, [selectedAntardasha, selectedPratyantardashaKey, dashaData]);

  const sookshmaDashas = React.useMemo(() => selectedPratyantardasha ? calculateDashaSubPeriods(selectedPratyantardasha, 'Sookshma') : [], [selectedPratyantardasha]);
  const selectedSookshma = React.useMemo(() => sookshmaDashas.find((entry) => getDashaEntryKey(entry) === selectedSookshmaKey)
    || (getDashaEntryKey(selectedPratyantardasha) === getDashaEntryKey(dashaData?.currentPratyantardasha) ? dashaData?.currentSookshmaDasha : null)
    || sookshmaDashas[0]
    || null, [sookshmaDashas, selectedSookshmaKey, selectedPratyantardasha, dashaData]);
  const pranaDashas = React.useMemo(() => selectedSookshma ? calculateDashaSubPeriods(selectedSookshma, 'Prana') : [], [selectedSookshma]);
  const selectedPrana = React.useMemo(() => pranaDashas.find((entry) => getDashaEntryKey(entry) === selectedPranaKey)
    || (getDashaEntryKey(selectedSookshma) === getDashaEntryKey(dashaData?.currentSookshmaDasha) ? dashaData?.currentPranaDasha : null)
    || pranaDashas[0]
    || null, [pranaDashas, selectedPranaKey, selectedSookshma, dashaData]);

  React.useEffect(() => {
    if (!dashaData?.currentMahadasha) return;

    const defaultMahadashaKey = getDashaEntryKey(dashaData.currentMahadasha);
    if (!selectedMahadashaKey) {
      setSelectedMahadashaKey(defaultMahadashaKey);
    }
  }, [dashaData, selectedMahadashaKey]);

  React.useEffect(() => {
    if (!selectedMahadasha) return;

    const hasSelectedADInCurrentMD = selectedMahadasha.antardashas?.some(
      (entry: any) => getDashaEntryKey(entry) === selectedAntardashaKey
    );

    if (hasSelectedADInCurrentMD) {
      return;
    }

    const matchingCurrentAD = selectedMahadasha.antardashas?.find((entry: any) =>
      entry.lord === dashaData?.currentAntardasha?.lord &&
      entry.start?.getTime?.() === dashaData?.currentAntardasha?.start?.getTime?.()
    );

    const defaultAntardasha = matchingCurrentAD || selectedMahadasha.antardashas?.[0] || null;
    const defaultAntardashaKey = getDashaEntryKey(defaultAntardasha);

    if (defaultAntardashaKey && defaultAntardashaKey !== selectedAntardashaKey) {
      setSelectedAntardashaKey(defaultAntardashaKey);
    }
  }, [selectedMahadasha, dashaData, selectedAntardashaKey]);

  React.useEffect(() => {
    if (!selectedAntardasha) return;

    const hasSelectedPDInCurrentAD = selectedAntardasha.pratyantardashas?.some(
      (entry: any) => getDashaEntryKey(entry) === selectedPratyantardashaKey
    );

    if (hasSelectedPDInCurrentAD) {
      return;
    }

    const matchingCurrentPD = selectedAntardasha.pratyantardashas?.find((entry: any) =>
      entry.lord === dashaData?.currentPratyantardasha?.lord &&
      entry.start?.getTime?.() === dashaData?.currentPratyantardasha?.start?.getTime?.()
    );

    const defaultPratyantardasha = matchingCurrentPD || selectedAntardasha.pratyantardashas?.[0] || null;
    const defaultPratyantardashaKey = getDashaEntryKey(defaultPratyantardasha);

    if (defaultPratyantardashaKey && defaultPratyantardashaKey !== selectedPratyantardashaKey) {
      setSelectedPratyantardashaKey(defaultPratyantardashaKey);
    }
  }, [selectedAntardasha, dashaData, selectedPratyantardashaKey]);

  const normalizePlanetName = (name?: string | null) => (name || '').trim().toLowerCase();

  const getDashaLevelMeta = (level: 'M' | 'A' | 'P') => ({
    M: { bg: '#ede7f6', color: '#6a1b9a', border: '#b39ddb', label: 'Mahadasha' },
    A: { bg: '#f3e5f5', color: '#8e24aa', border: '#ce93d8', label: 'Antardasha' },
    P: { bg: '#f8eafc', color: '#ab47bc', border: '#e1bee7', label: 'Pratyantardasha' }
  }[level]);

  const getDashaEntryForLevel = (level: 'M' | 'A' | 'P') => ({
    M: selectedMahadasha,
    A: selectedAntardasha,
    P: selectedPratyantardasha,
  }[level]);

  const renderDashaMarker = (level: 'M' | 'A' | 'P') => {
    const meta = getDashaLevelMeta(level);
    const period = getDashaEntryForLevel(level);
    const endDate = formatDisplayDate(period?.end, { fallback: '-' });
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 24,
          height: 24,
          px: 0.9,
          gap: 0.5,
          borderRadius: 999,
          bgcolor: meta.bg,
          color: meta.color,
          border: `1px solid ${meta.border}`,
          fontSize: '0.75rem',
          fontWeight: 'bold'
        }}
      >
        <Box component="span">{level}</Box>
        <Box component="span" sx={{ fontWeight: 600, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
          Ends {endDate}
        </Box>
      </Box>
    );
  };

  const getDashaMarkersForPlanet = (planetName?: string | null) => {
    const normalizedName = normalizePlanetName(planetName);
    const markers: Array<'M' | 'A' | 'P'> = [];

    if (!normalizedName) return markers;
    if (normalizePlanetName(selectedMahadasha?.lord) === normalizedName) markers.push('M');
    if (normalizePlanetName(selectedAntardasha?.lord) === normalizedName) markers.push('A');
    if (normalizePlanetName(selectedPratyantardasha?.lord) === normalizedName) markers.push('P');

    return markers;
  };

  const allKPCusps = React.useMemo(() => {
    const kpCusps = chartData?.kpAnalysis?.kpCusps;
    if (!kpCusps) {
      return [];
    }

    return Object.values(kpCusps)
      .sort((a: any, b: any) => Number(a?.houseNumber || 0) - Number(b?.houseNumber || 0));
  }, [chartData]);

  const orderedPlanetaryEntries = React.useMemo(() => {
    const planetarySignificators = chartData?.nadiAnalysis?.planetarySignificators || {};
    const entries = Object.entries(planetarySignificators) as [string, any][];

    const dashaOrder = [
      'KETU',
      'VENUS',
      'SUN',
      'MOON',
      'MARS',
      'RAHU',
      'JUPITER',
      'SATURN',
      'MERCURY',
      'URANUS',
      'NEPTUNE',
      'PLUTO'
    ];

    const dashaOrderMap = new Map(dashaOrder.map((planet, index) => [planet, index]));

    return entries.sort(([keyA, dataA], [keyB, dataB]) => {
      const planetA = String(dataA?.planet || keyA || '').toUpperCase();
      const planetB = String(dataB?.planet || keyB || '').toUpperCase();

      const orderA = dashaOrderMap.has(planetA) ? dashaOrderMap.get(planetA)! : Number.MAX_SAFE_INTEGER;
      const orderB = dashaOrderMap.has(planetB) ? dashaOrderMap.get(planetB)! : Number.MAX_SAFE_INTEGER;

      if (orderA !== orderB) return orderA - orderB;
      return planetA.localeCompare(planetB);
    });
  }, [chartData]);

  const getPlanetShortName = (planetName?: string | null) => {
    return getPlanetAbbr(t, String(planetName || ''));
  };

  const planetaryRashiRows = React.useMemo(() => {
    const planetOrder = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU', 'URANUS', 'NEPTUNE', 'PLUTO'];
    const ascCusp = chartData?.kpAnalysis?.kpCusps?.['1'] || chartData?.kpAnalysis?.kpCusps?.[1];

    const rows: Array<{ label: string; rl: string; nl: string; sl: string }> = [];

    if (chartData?.houses?.ascendant) {
      rows.push({
        label: t('astrology.ascendant'),
        rl: getPlanetShortName(ascCusp?.cuspLord || chartData.houses.ascendant.sign?.ruler),
        nl: getPlanetShortName(ascCusp?.starLord || chartData.houses.ascendant.nakshatra?.lord),
        sl: getPlanetShortName(ascCusp?.subLord || chartData.houses.ascendant.subLord)
      });
    }

    const sortedPlanets = Object.entries(chartData?.planets || {})
      .filter(([key, planet]: [string, any]) => shouldShowPlanet(key, planet))
      .map(([, planet]) => planet)
      .sort((a: any, b: any) => {
      const indexA = planetOrder.indexOf(String(a?.name || '').toUpperCase());
      const indexB = planetOrder.indexOf(String(b?.name || '').toUpperCase());
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    sortedPlanets.forEach((planet: any) => {
      const kpPlanetData = chartData?.kpAnalysis?.kpPlanetarySignificators?.[String(planet?.name || '').toUpperCase()];
      rows.push({
        label: getPlanetAbbr(t, planet?.name || '-'),
        rl: getPlanetShortName(planet?.sign?.ruler),
        nl: getPlanetShortName(kpPlanetData?.starLord || planet?.nakshatra?.lord),
        sl: getPlanetShortName(kpPlanetData?.subLord || planet?.subLord)
      });
    });

    return rows;
  }, [chartData, t]);

  const renderLiveDashaSection = () => {
    if (!dashaData) return null;

    return (
      <Box sx={{ mt: 3, p: 1.5, bgcolor: '#faf5ff', border: '2px solid #7b1fa2', borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#7b1fa2', fontWeight: 'bold', mb: 1.5 }}>
          {t('astrology.vimshottariDashaTimeline')}
        </Typography>

        {selectedMahadasha && selectedAntardasha && selectedPratyantardasha && selectedSookshma && selectedPrana && (
          <CollapsibleDashaTimeline
            timeline={dashaData}
            selected={{ md:selectedMahadasha, ad:selectedAntardasha, pd:selectedPratyantardasha, sookshma:selectedSookshma, prana:selectedPrana }}
            current={{ md:dashaData.currentMahadasha, ad:dashaData.currentAntardasha, pd:dashaData.currentPratyantardasha, sookshma:dashaData.currentSookshmaDasha, prana:dashaData.currentPranaDasha }}
            children={{ sookshma:sookshmaDashas, prana:pranaDashas }}
            labels={{ md:t('astrology.mahadasha', { defaultValue:'Mahadasha' }), ad:t('astrology.antardasha', { defaultValue:'Antardasha' }), pd:t('astrology.pratyantardasha', { defaultValue:'Pratyantardasha' }), sookshma:'Sookshma Dasha', prana:'Prana Dasha', current:t('common.current', { defaultValue:'Current' }) }}
            planet={(entry) => getPlanetAbbr(t, entry.lord)}
            onSelect={{
              md:(entry) => { setSelectedMahadashaKey(getDashaEntryKey(entry)); setSelectedAntardashaKey(null); setSelectedPratyantardashaKey(null); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              ad:(entry) => { setSelectedAntardashaKey(getDashaEntryKey(entry)); setSelectedPratyantardashaKey(null); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              pd:(entry) => { setSelectedPratyantardashaKey(getDashaEntryKey(entry)); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              sookshma:(entry) => { setSelectedSookshmaKey(getDashaEntryKey(entry)); setSelectedPranaKey(null); },
              prana:(entry) => setSelectedPranaKey(getDashaEntryKey(entry)),
            }}
          />
        )}
      </Box>
    );
  };

  const renderPlanetaryRashiSection = () => {
    if (!planetaryRashiRows.length) {
      return null;
    }

    return (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5fbf7', border: '2px solid #81c784', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: '#2e7d52', fontWeight: 'bold', mb: 1.25 }}>
          {t('astrology.planetaryPositionsForRashi')}
        </Typography>
        <TableContainer sx={{ maxHeight: 360, bgcolor: 'white', border: '1px solid #c8e6c9', borderRadius: 2 }}>
          <Table stickyHeader size="small" aria-label="planetary lords table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('astrology.planet')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>RL</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>NL</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>SL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planetaryRashiRows.map((row) => (
                <TableRow key={`planet-row-${row.label}`} hover>
                  <TableCell sx={{ fontWeight: row.label === 'Lagna' ? 'bold' : 500 }}>{row.label}</TableCell>
                  <TableCell>{row.rl || '-'}</TableCell>
                  <TableCell>{row.nl || '-'}</TableCell>
                  <TableCell>{row.sl || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderKPCuspsSection = () => {
    if (!allKPCusps.length) {
      return (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5fbf7', border: '2px solid #81c784', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: '#2e7d52', fontWeight: 'bold', mb: 1 }}>
            {t('astrology.kpCuspsAllHouses')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('astrology.kpCuspDataUnavailable')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5fbf7', border: '2px solid #81c784', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: '#2e7d52', fontWeight: 'bold', mb: 1.25 }}>
          {t('astrology.kpCuspsAllHouses')}
        </Typography>
        <TableContainer sx={{ maxHeight: 360, bgcolor: 'white', border: '1px solid #c8e6c9', borderRadius: 2 }}>
          <Table stickyHeader size="small" aria-label="KP cusp lords table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('astrology.house', { number: '' }).trim()}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('astrology.cuspLord')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('astrology.starLord')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('astrology.subLord')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 12 }, (_, index) => {
                const displayHouse = index + 1;
                const actualHouse = getActualHouseForDisplay(displayHouse);
                const kpCusps = chartData?.kpAnalysis?.kpCusps || {};
                const cusp = kpCusps[actualHouse] || kpCusps[String(actualHouse)];
                if (!cusp) return null;

                return (
                <TableRow key={`cusp-row-${displayHouse}`} hover>
                  <TableCell>
                    {displayHouse}{actualHouse !== displayHouse ? ` / H${actualHouse}` : ''}
                  </TableCell>
                  <TableCell>{getPlanetAbbr(t, cusp.cuspLord || '') || '-'}</TableCell>
                  <TableCell>{getPlanetAbbr(t, cusp.starLord || '') || '-'}</TableCell>
                  <TableCell>{getPlanetAbbr(t, cusp.subLord || '') || '-'}</TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!chartData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Chart not found</Alert>
      </Container>
    );
  }

  const isHoraryChart = chartData?.chartType === 'horary';
  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: '#fafafa', minHeight: '100vh' }}>
      {/* Navigation to separated analysis pages */}
      <AnalysisTabBar chartId={id!} active="nadi" isHoraryChart={isHoraryChart} />

      {/* Preview Notice */}
      {id === 'preview' && (
        <Alert severity="info" sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText', border: '1px solid', borderColor: 'primary.main' }}>
          This temporary preview could not be auto-saved. Please retry the calculation if you want a permanent chart link.
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 3, bgcolor: 'primary.main', p: 2, borderRadius: 2, border: '2px solid', borderColor: 'primary.dark' }}>
        {/* Branding */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: 2, 
            p: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: 1
          }}>
            <img 
              src="/logo.png" 
              alt="PoojaAstro Logo" 
              style={{ height: '40px', width: 'auto' }}
            />
          </Box>
           <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', margin: 0 }}>
            PoojaAstro - Nadi Software
          </Typography> 
        </Box> */}
        
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                {displayChartName(chartData.birthDetails?.name)}
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.9)">
                {formatDate(chartData.birthDetails.dateOfBirth)} • {formatTime(chartData.birthDetails.timeOfBirth)} • {chartData.birthDetails.placeOfBirth.city}
              </Typography>
              {/* PDF download buttons hidden */}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: 2,
                p: 1.25,
                width: 'fit-content',
                maxWidth: '100%',
                ml: { xs: 0, md: 'auto' }
              }}>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  {t('astrology.birthTimeRectification')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mb: 1 }}>
                  {t('astrology.updatedDateTime')}: {getRectifiedDateTimeLabel()}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', width: 'fit-content', maxWidth: '100%' }}>
                  {[
                    { label: t('astrology.month'), unit: 'month' as const },
                    { label: t('astrology.day'), unit: 'day' as const },
                    { label: t('astrology.hours'), unit: 'hour' as const },
                    { label: t('astrology.minutes'), unit: 'minute' as const },
                    { label: t('astrology.secondsShort'), unit: 'second' as const }
                  ].map((item) => (
                    <Box key={item.unit} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.12)' }}>
                      <Typography variant="caption" sx={{ color: 'white', minWidth: 44, fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleRectificationAdjust(item.unit, -1)}
                        disabled={rectificationLoading || saveRectificationLoading}
                        sx={{ minWidth: 28, px: 0, bgcolor: 'rgba(255,255,255,0.9)', color: 'primary.main', '&:hover': { bgcolor: 'white' } }}
                      >
                        -
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleRectificationAdjust(item.unit, 1)}
                        disabled={rectificationLoading || saveRectificationLoading}
                        sx={{ minWidth: 28, px: 0, bgcolor: 'rgba(255,255,255,0.9)', color: 'primary.main', '&:hover': { bgcolor: 'white' } }}
                      >
                        +
                      </Button>
                    </Box>
                  ))}

                  {!isHoraryChart && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSaveRectification}
                    disabled={rectificationLoading || saveRectificationLoading}
                    sx={{ ml: { xs: 0, md: 0.5 }, fontWeight: 600 }}
                  >
                    {saveRectificationLoading ? t('astrology.saving') : t('astrology.save')}
                  </Button>
                  )}

                  {rectificationLoading && (
                    <Typography variant="caption" sx={{ color: 'white' }}>
                      {t('astrology.recalculating')}
                    </Typography>
                  )}
                </Box>

                {rectificationMessage && (
                  <Alert severity={rectificationMessage.toLowerCase().includes('success') ? 'success' : 'info'} sx={{ mt: 1 }}>
                    {rectificationMessage}
                  </Alert>
                )}
              </Box>
          </Grid>
        </Grid>
        <Box sx={{
          mt: 1.5,
          pt: 1.25,
          borderTop: '1px solid rgba(255,255,255,0.18)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.88)', fontWeight: 600, mr: 0.5 }}>
            Ruling Planets · {rulingLocation.city}
          </Typography>
          {currentRulingPlanets ? rulingPlanetItems(currentRulingPlanets).map((item) => (
            <Chip
              key={item.label}
              size="small"
              label={`${item.label}: ${getPlanetAbbr(t, item.value || '-')}`}
              title={item.title}
              sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white', border: '1px solid rgba(255,255,255,0.25)' }}
            />
          )) : (
            <Typography variant="caption" sx={{ color: currentRulingError ? '#ffd6d6' : 'rgba(255,255,255,0.86)' }}>
              {currentRulingError || 'Loading ruling planets...'}
            </Typography>
          )}
        </Box>
      </Box>

      {isHoraryChart && chartData.horaryMeta && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              Horary Details
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2"><strong>Seed Number:</strong> {chartData.horaryMeta.seedNumber}</Typography>
                <Typography variant="body2"><strong>Question:</strong> {chartData.horaryMeta.question || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Fixed Ascendant:</strong> {chartData.horaryMeta.seedDetails?.sign?.name} {chartData.horaryMeta.seedDetails?.degree?.formatted}</Typography>
                <Typography variant="body2"><strong>Star Lord:</strong> {chartData.horaryMeta.seedDetails?.starLord}</Typography>
                <Typography variant="body2"><strong>Sub Lord:</strong> {chartData.horaryMeta.seedDetails?.subLord}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ mb: 1 }}><strong>Ruling Planets:</strong></Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[chartData.horaryMeta.rulingPlanets?.lagnaLord, chartData.horaryMeta.rulingPlanets?.lagnaStarLord, chartData.horaryMeta.rulingPlanets?.moonSignLord, chartData.horaryMeta.rulingPlanets?.moonStarLord, chartData.horaryMeta.rulingPlanets?.dayLord]
                    .filter(Boolean)
                    .map((planet: string, index: number) => (
                      <Chip key={`${planet}-${index}`} label={planet} color="primary" variant="outlined" size="small" />
                    ))}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

        {/* Enhanced Natal Analysis Section */}
        {/* <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
              border: '2px solid #f8bbd9'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#c2185b', fontWeight: 'bold', mb: 2 }}>
                  🌅 Day Lord Analysis
                </Typography>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid #f8bbd9'
                }}>
                  <Typography variant="h5" sx={{ color: '#ad1457', fontWeight: 'bold' }}>
                    {chartData.nadiAnalysis?.dayLord?.lord || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="#c2185b">
                    Ruling Planet
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid >
            <Card sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
              border: '2px solid #81c784'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#388e3c', fontWeight: 'bold', mb: 2 }}>
                  🎯 Ascendant Chain
                </Typography>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid #81c784'
                }}>
                  <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: '500' }}>
                    {chartData.nadiAnalysis?.ascendantChain?.chain || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="#388e3c">
                    Personality Indicators
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid >
            <Card sx={{ 
              height: '100%',
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              border: '2px solid #ffb74d'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 'bold', mb: 2 }}>
                  🌙 Moon Chain
                </Typography>
                <Box sx={{ 
                  bgcolor: 'white', 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid #ffb74d'
                }}>
                  <Typography variant="body1" sx={{ color: '#ef6c00', fontWeight: '500' }}>
                    {chartData.nadiAnalysis?.moonChain?.chain || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="#f57c00">
                    Emotional Patterns
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid> */}

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={selectedChartTab}
          onChange={handleChartTabChange}
          aria-label="nadi analysis tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('astrology.nadiAnalysis')} />
          <Tab label={translateNadiEventText(i18n.resolvedLanguage || i18n.language, 'Event Analysis')} />
          <Tab label={dashaAnalysisLabel(i18n.resolvedLanguage || i18n.language)} />
        </Tabs>
      </Card>

      {selectedChartTab === 2 ? (
        <DashaAnalysisPanel chart={chartData} system="UMANG_NADI" />
      ) : selectedChartTab === 1 ? (
        <EventAnalysisPanel chart={chartData} isPremium={isPremiumUser} rotationHouse={chartRotationHouse} />
      ) : (
      /* Main Layout: Chart + Details */
      <Grid container spacing={3}>
        {/* Left Side - Chart */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card>
            <CardContent>
              {/* Chart Display */}
              <BirthChartDisplay
                chartData={chartData}
                rotationHouse={chartRotationHouse}
                onRotationHouseChange={setChartRotationHouse}
              />
              
              <Divider sx={{ my: 2 }} />

              {/* Birth Details */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isHoraryChart ? t('astrology.queryDetails') : t('astrology.birthDetails')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>{t('astrology.date')}:</strong> {formatDate(chartData.birthDetails.dateOfBirth)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('astrology.time')}:</strong> {formatTime(chartData.birthDetails.timeOfBirth)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('astrology.place')}:</strong> {chartData.birthDetails.placeOfBirth.city}, {chartData.birthDetails.placeOfBirth.country}
                </Typography>
                {!isHoraryChart && (
                  <Typography variant="body2">
                    <strong>{t('astrology.gender')}:</strong> {chartData.birthDetails.gender}
                  </Typography>
                )}
                {isHoraryChart && (
                  <Typography variant="body2">
                    <strong>{t('astrology.seed')}:</strong> {chartData.horaryMeta?.seedNumber}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>{t('astrology.ayanamshaKp')}:</strong> {chartData.calculationData?.ayanamsha?.toFixed(4)}°
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Planetary & House Details */}
        <Grid size={{ xs: 12, lg: 7 }}>
          {/* Nakshatra Nadi Display - 3-Level System */}
          <Card sx={{ mb: 2, border: '2px solid #2e7d52', borderRadius: 3 }}>
            <CardContent sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d52', fontWeight: 'bold' }}>
                {t('astrology.nakshatraNadi')} 
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('astrology.planetStarSub')}
              </Typography>

              {/* Life Event Dropdown */}
              <FormControl fullWidth sx={{ 
                mb: 2, 
                '& .MuiOutlinedInput-root': { 
                  '&:hover fieldset': { borderColor: 'primary.main' }, 
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' } 
                }, 
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' } 
              }}>
                <InputLabel id="life-event-label">{t('astrology.lifeEvent')}</InputLabel>
                <Select
                  labelId="life-event-label"
                  id="life-event-select"
                  value={effectiveLifeEvent}
                  label={t('astrology.lifeEvent')}
                  onChange={handleLifeEventChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400
                      }
                    }
                  }}
                >
                  {Object.entries(lifeEvents).map(([key, event]) => (
                    <MenuItem key={key} value={key} disabled={!isPremiumUser && !FREE_LIFE_EVENT_KEYS.has(key)}>
                      {event.label}
                      {!isPremiumUser && !FREE_LIFE_EVENT_KEYS.has(key) ? ' (Locked)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isPremiumUser && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 2 }}>
                  Upgrade to unlock more life events
                </Typography>
              )}
              
              {/* Color Legend */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ color: 'primary.main' }}>
                  {t('astrology.houseSignificanceFor', { event: lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents]?.label })}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents]?.goodHouses.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: '#4caf50', 
                        borderRadius: '50%' 
                      }} />
                      <Typography variant="caption" fontWeight="bold">
                        {t('astrology.good')}: {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents].goodHouses.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents]?.goodSupportiveHouses.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: '#2196f3',
                        borderRadius: '50%' 
                      }} />
                      <Typography variant="caption" fontWeight="bold">
                        {t('astrology.goodSupportive')}: {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents].goodSupportiveHouses.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents]?.badHouses.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: '#f44336', 
                        borderRadius: '50%' 
                      }} />
                      <Typography variant="caption" fontWeight="bold">
                        {t('astrology.bad')}: {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents].badHouses.join(', ')}
                      </Typography>
                    </Box>
                  )}
                  {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents]?.badSupportiveHouses.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: '#8d6e63', 
                        borderRadius: '50%' 
                      }} />
                      <Typography variant="caption" fontWeight="bold">
                        {t('astrology.badSupportive')}: {lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents].badSupportiveHouses.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                {orderedPlanetaryEntries.map(([planetKey, data]: [string, any]) => {
                  if (!data) return null;

                  const getAbbr = (name: string | undefined | null) => getPlanetAbbr(t, name || '');

                  // Helper function to render houses with highlighting
                  const renderHousesWithHighlight = (houses: number[], occupiedHouse?: number) => {
                    const event = lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents];

                    return rotateHouseList(houses || []).map((house, index) => {
                      let color = 'inherit';
                      let bgColor = 'transparent';
                      let fontWeight = 'normal';

                      if (event && effectiveLifeEvent !== 'none') {
                        if (event.goodHouses.includes(house)) {
                          color = 'white';
                          bgColor = '#4caf50';
                          fontWeight = 'bold';
                        } else if (event.badHouses.includes(house)) {
                          color = 'white';
                          bgColor = '#f44336';
                          fontWeight = 'bold';
                        } else if (event.goodSupportiveHouses.includes(house)) {
                          color = 'white';
                          bgColor = '#2196f3';
                          fontWeight = 'bold';
                        } else if (event.badSupportiveHouses.includes(house)) {
                          color = 'white';
                          bgColor = '#8d6e63';
                          fontWeight = 'bold';
                        }
                      }

                      return (
                        <Box
                          key={`${house}-${index}`}
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: bgColor !== 'transparent' ? '22px' : 'auto',
                            height: bgColor !== 'transparent' ? '22px' : 'auto',
                            mr: 0.5,
                            px: bgColor === 'transparent' ? 0 : 0.5,
                            borderRadius: bgColor !== 'transparent' ? '50%' : 0,
                            bgcolor: bgColor,
                            color,
                            fontSize: '0.875rem',
                            fontWeight: Number(house) === Number(occupiedHouse) ? 'bold' : fontWeight
                          }}
                        >
                          {house}
                        </Box>
                      );
                    }) || [];
                  };

                  const planetName = data.planet;
                  const starLord = data.starLord;
                  const subLord = data.subLord;
                  
                  const getPlanetLevelSignificators = (name: string | undefined | null) => {
                    if (!name) return [];
                    const matchingEntry = Object.values(chartData.nadiAnalysis?.planetarySignificators || {}).find(
                      (entry: any) => entry?.planet === name
                    ) as any;
                    return matchingEntry?.significators?.planet || [];
                  };
                  const getPlanetCurrentHouse = (name: string | undefined | null) => {
                    if (!name) return undefined;
                    const matchingEntry = Object.values(chartData.nadiAnalysis?.planetarySignificators || {}).find(
                      (entry: any) => String(entry?.planet || '').toLowerCase() === String(name).toLowerCase()
                    ) as any;
                    return matchingEntry?.currentHouse ? toRotatedHouse(Number(matchingEntry.currentHouse)) : undefined;
                  };

                  // Always prefer the source planet's own significators so occupation house is shown
                  // even for older saved charts where star/sub arrays were stored without it.
                  const planetSigs = getPlanetLevelSignificators(planetName).length > 0
                    ? getPlanetLevelSignificators(planetName)
                    : (data.significators?.planet || []);
                  const starLordSigs = getPlanetLevelSignificators(starLord).length > 0
                    ? getPlanetLevelSignificators(starLord)
                    : (data.significators?.starLord || []);
                  const subLordSigs = getPlanetLevelSignificators(subLord).length > 0
                    ? getPlanetLevelSignificators(subLord)
                    : (data.significators?.subLord || []);
                  const actualCurrentHouse = data.currentHouse || chartData.planets?.[planetKey]?.bhavaChalitHouse || chartData.planets?.[planetKey]?.house;
                  const currentHouse = actualCurrentHouse ? toRotatedHouse(Number(actualCurrentHouse)) : actualCurrentHouse;
                  const dashaMarkers = getDashaMarkersForPlanet(planetName);
                  const isDashaHighlighted = dashaMarkers.length > 0;

                  return (
                    <Grid key={planetKey} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box sx={{ 
                        p: 1.25,
                        bgcolor: isDashaHighlighted ? '#fcf8ff' : 'white',
                        border: `2px solid ${isDashaHighlighted ? '#ce93d8' : '#e0e0e0'}`,
                        borderRadius: 2,
                        boxShadow: isDashaHighlighted ? '0 4px 12px rgba(123,31,162,0.12)' : '0 2px 4px rgba(0,0,0,0.1)',
                        minHeight: '110px'
                      }}>
                        {/* Planet Name Header */}
                        <Typography  sx={{ color: '#2e7d52', fontWeight: 'bold', textAlign: 'center', mb: isDashaHighlighted ? 0.5 : 1, textDecoration: 'underline' }}>
                          {getPlanetAbbr(t, planetName)}{currentHouse ? ` (${getHouseShort(t, currentHouse)}${actualCurrentHouse && Number(actualCurrentHouse) !== Number(currentHouse) ? `/${getHouseShort(t, actualCurrentHouse)}` : ''})` : ''}
                        </Typography>

                        {isDashaHighlighted && (
                          <Stack direction="row" spacing={0.5} justifyContent="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                            {dashaMarkers.map((marker) => (
                              <React.Fragment key={`${planetName}-${marker}`}>
                                {renderDashaMarker(marker)}
                              </React.Fragment>
                            ))}
                          </Stack>
                        )}

                        {/* <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.75 }}>
                          <Box sx={{ px: 1, py: 0.35, bgcolor: '#e8f5e9', border: '1px solid #81c784', borderRadius: 5 }}>
                            <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '0.72rem' }}>
                              Present in House {currentHouse || 'N/A'}
                            </Typography>
                          </Box>
                        </Box> */}
                        
                        {/* Star and Sub info */}
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          textAlign: 'center', 
                          color: 'text.secondary', 
                          fontStyle: 'italic',
                          mb: 1
                        }}>
                          {/* Star: {starLord} • Sub: {subLord} */}
                        </Typography>
                        
                        {/* Planet's significators */}
                        <Typography variant="body2" sx={{ mb: 0.35 }}>
                          <strong>{getAbbr(planetName)}</strong> {renderHousesWithHighlight(planetSigs, currentHouse ? Number(currentHouse) : undefined)}
                        </Typography>
                        
                        {/* Star Lord's significators */}
                        <Typography variant="body2" sx={{ mb: 0.35 }}>
                          <strong>{getAbbr(starLord)}</strong> {renderHousesWithHighlight(starLordSigs, getPlanetCurrentHouse(starLord))}
                        </Typography>
                        
                        {/* Sub Lord's significators */}
                        <Typography variant="body2">
                          <strong>{getAbbr(subLord)}</strong> {renderHousesWithHighlight(subLordSigs, getPlanetCurrentHouse(subLord))}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {renderLiveDashaSection()}
              {renderPlanetaryRashiSection()}
              {renderKPCuspsSection()}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant={showMoreDetails ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setShowMoreDetails((prev) => !prev)}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 'bold', px: 3 }}
            >
              {showMoreDetails ? 'See less details' : 'See more details'}
            </Button>
          </Box>

          {showMoreDetails && (
            <>
          {/* Ascendant & Special Points */}
          <Card sx={{ mb: 2, border: '2px solid #2e7d52', borderRadius: 3 }}>
            <CardContent sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d52', fontWeight: 'bold' }}>
                Ascendant & Special Points
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">Ascendant</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {chartData.houses?.ascendant?.sign?.name} {chartData.houses?.ascendant?.degree?.formatted}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">Midheaven (MC)</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {chartData.houses?.midheaven?.sign?.name} {chartData.houses?.midheaven?.degree?.formatted}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2" color="text.secondary">Nakshatra</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {chartData.houses?.ascendant?.nakshatra?.name}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Planetary Positions */}
          <Card sx={{ mb: 2, border: '2px solid #2e7d52', borderRadius: 3 }}>
            <CardContent sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d52', fontWeight: 'bold' }}>
                Planetary Positions
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(chartData.planets || {}).filter(([key, planet]: [string, any]) => shouldShowPlanet(key, planet)).map(([key, planet]: [string, any]) => (
                  <Grid key={key}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #2e7d52' }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="#2e7d52">
                        {planet.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Sign:</strong> {planet.sign?.name} • <strong>House:</strong> {planet.house}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Degree:</strong> {planet.degree?.formatted}
                      </Typography>
                      <Typography variant="caption" display="block">
                        <strong>Nakshatra:</strong> {planet.nakshatra?.name} {planet.nakshatra?.lord ? `(${planet.nakshatra.lord})` : ''}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* House Cusps */}
          <Card sx={{ mb: 2, border: '2px solid #2e7d52', borderRadius: 3 }}>
            <CardContent sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d52', fontWeight: 'bold' }}>
                House Cusps (Placidus)
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                flexWrap: 'wrap', 
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {Object.entries(chartData.houses?.cusps || {}).map(([houseNum, cusp]: [string, any]) => (
                  <Box key={houseNum} sx={{ 
                    p: 1.5, 
                    bgcolor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 70,
                    height: 70,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    border: '2px solid',
                    borderColor: 'primary.light'
                  }}>
                    <Typography variant="caption" fontWeight="bold">
                      {houseNum}
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {Math.floor(cusp.sign?.degrees || 0)}°
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                      {cusp.sign?.name?.substring(0, 3)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Bhava Chalit (Equal House) */}
          <Card sx={{ mb: 2, border: '2px solid #2e7d52', borderRadius: 3 }}>
            <CardContent sx={{ bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2e7d52', fontWeight: 'bold' }}>
                Bhava Chalit (Equal House System)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {chartData.bhavaChalit?.system}
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(chartData.bhavaChalit?.cusps || {}).map(([houseNum, cusp]: [string, any]) => (
                  <Grid key={houseNum}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold" color="primary">
                        Bhava {houseNum}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {cusp.sign?.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {cusp.degree?.formatted}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          
                
             
          

          {/* House Cusps - Old Style */}
          <Card sx={{ mb: 2, display: 'none' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                House Cusps
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(chartData.houses?.cusps || {}).map(([houseNum, cusp]: [string, any]) => (
                  <Grid key={houseNum}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold" color="primary">
                        House {houseNum}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {cusp.sign?.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {cusp.degree?.formatted}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {cusp.nakshatra?.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* KP Cuspal Significators (4-Level System) */}
          {chartData.kpAnalysis?.kpCuspalSignificators && (
            <Card sx={{ mb: 2, border: '2px solid #1976d2', borderRadius: 3 }}>
              <CardContent sx={{ bgcolor: '#f0f8ff' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  KP Cuspal Significators (4-Level System)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Authentic KP 4-level cuspal rule in priority order: Star Lord → Planets in Star → Sign Lord → Planets in Sign
                </Typography>
                
                <Grid container spacing={1}>
                  {Object.entries(chartData.kpAnalysis.kpCuspalSignificators).map(([houseNum, cuspalData]: [string, any]) => (
                    <Grid key={houseNum}>
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: '#1976d2',
                        borderRadius: 1
                      }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                          House {houseNum}
                        </Typography>
                        
                        {/* Cusp Info */}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {cuspalData.cuspSignName} • Star: {cuspalData.cuspStarLord}
                        </Typography>

                        {/* L1: Star Lord Houses - Highest Priority */}
                        {cuspalData.levels?.level1_starLord?.length > 0 && (
                          <Box sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#d32f2f' }} display="block">
                              L1: {cuspalData.levels.level1_starLord.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* L2: Planets in Star */}
                        {cuspalData.levels?.level2_planetsInStar?.length > 0 && (
                          <Box sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#7b1fa2' }} display="block">
                              L2: {cuspalData.levels.level2_planetsInStar.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* L3: Sign Lord Houses */}
                        {cuspalData.levels?.level3_signLord?.length > 0 && (
                          <Box sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#388e3c' }} display="block">
                              L3: {cuspalData.levels.level3_signLord.join(', ')}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* L4: Planets in Sign */}
                        {cuspalData.levels?.level4_planetsInSign?.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ff9800' }} display="block">
                              L4: {cuspalData.levels.level4_planetsInSign.join(', ')}
                            </Typography>
                          </Box>
                        )}

                        {/* Combined Houses with Event Highlighting */}
                        {(cuspalData.levels?.level1_starLord?.length > 0 || 
                          cuspalData.levels?.level2_planetsInStar?.length > 0 || 
                          cuspalData.levels?.level3_signLord?.length > 0 || 
                          cuspalData.levels?.level4_planetsInSign?.length > 0) && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', mr: 0.5 }}>
                              All: 
                            </Typography>
                            {[
                              ...(cuspalData.levels?.level1_starLord || []),
                              ...(cuspalData.levels?.level2_planetsInStar || []),
                              ...(cuspalData.levels?.level3_signLord || []),
                              ...(cuspalData.levels?.level4_planetsInSign || [])
                            ].map((house, index) => {
                              const event = lifeEvents[effectiveLifeEvent as keyof typeof lifeEvents];
                              let bgColor = 'transparent';
                              let color = 'primary.main';
                              
                              if (event && effectiveLifeEvent !== 'none') {
                                if (event.goodHouses.includes(house)) {
                                  bgColor = '#4caf50';
                                  color = 'white';
                                } else if (event.badHouses.includes(house)) {
                                  bgColor = '#f44336';
                                  color = 'white';
                                } else if (event.goodSupportiveHouses.includes(house)) {
                                  bgColor = '#2196f3';
                                  color = 'white';
                                } else if (event.badSupportiveHouses.includes(house)) {
                                  bgColor = '#8d6e63';
                                  color = 'white';
                                }
                              }
                              
                              return (
                                <Box
                                  key={`${house}-${index}`}
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '18px',
                                    height: '18px',
                                    mr: 0.3,
                                    borderRadius: bgColor !== 'transparent' ? '50%' : 0,
                                    bgcolor: bgColor,
                                    color: color,
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    border: '1px solid #ddd'
                                  }}
                                >
                                  {house}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Legend */}
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, bgcolor: '#d32f2f', borderRadius: '50%' }}></Box>
                      <span>L1 (Star Lord - Highest)</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, bgcolor: '#7b1fa2', borderRadius: '50%' }}></Box>
                      <span>L2 (Planets in Star)</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, bgcolor: '#388e3c', borderRadius: '50%' }}></Box>
                      <span>L3 (Sign Lord)</span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, bgcolor: '#ff9800', borderRadius: '50%' }}></Box>
                      <span>L4 (Planets in Sign - Lowest)</span>
                    </Box>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
            </>
          )}
        </Grid>
      </Grid>
      )}

      {/* {!isHoraryChart && isPremiumUser && (
        <VedicYogasPanel yogas={yogas} loading={yogasLoading} error={yogasError} />
      )} */}

    </Container>
  );
};

export default ChartDisplay;
